import { useState, useCallback } from 'react'
import { createPortal } from 'react-dom'
import {
  ResponsiveGridLayout,
  useContainerWidth,
  type Layout,
  type LayoutItem,
  type ResponsiveLayouts,
} from 'react-grid-layout'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import { Pencil, Check, Plus } from 'lucide-react'
import 'react-grid-layout/css/styles.css'

export interface WidgetConfig {
  i: string // unique widget ID
  type: string // widget type key
  x: number
  y: number
  w: number
  h: number
  minW?: number
  minH?: number
}

interface WidgetGridProps {
  widgets: WidgetConfig[]
  onLayoutChange: (layout: WidgetConfig[]) => void
  renderWidget: (config: WidgetConfig, isEditing: boolean, onRemove: () => void) => React.ReactNode
  availableWidgets?: { type: string; label: string; defaultW: number; defaultH: number }[]
}

const DEFAULT_WIDGETS: WidgetConfig[] = [
  { i: 'clock', type: 'clock', x: 0, y: 0, w: 12, h: 2, minW: 6, minH: 2 },
  { i: 'my-tickets', type: 'my-tickets', x: 0, y: 2, w: 4, h: 8, minW: 3, minH: 4 },
  { i: 'my-tickets-mail', type: 'my-tickets-mail', x: 4, y: 2, w: 4, h: 8, minW: 3, minH: 4 },
  { i: 'my-tickets-changes', type: 'my-tickets-changes', x: 8, y: 2, w: 4, h: 8, minW: 3, minH: 4 },
  { i: 'unassigned-tickets', type: 'unassigned-tickets', x: 0, y: 10, w: 6, h: 8, minW: 3, minH: 4 },
  { i: 'all-tickets', type: 'all-tickets', x: 6, y: 10, w: 6, h: 8, minW: 3, minH: 4 },
]

export function useWidgetLayout() {
  const qc = useQueryClient()

  const { data } = useQuery({
    queryKey: ['dashboard', 'widgets'],
    queryFn: () => apiClient.get('/dashboard/widgets').then((r) => r.data),
    staleTime: Infinity,
  })

  const saveMutation = useMutation({
    mutationFn: (layout: WidgetConfig[]) =>
      apiClient.put('/dashboard/widgets', { layout }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['dashboard', 'widgets'] }),
  })

  const layout: WidgetConfig[] = Array.isArray(data?.layout) ? data.layout : DEFAULT_WIDGETS

  return { layout, save: saveMutation.mutate, isSaving: saveMutation.isPending }
}

function widgetConfigsToLayout(widgets: WidgetConfig[]): LayoutItem[] {
  return widgets.map((w) => ({
    i: w.i,
    x: w.x,
    y: w.y,
    w: w.w,
    h: w.h,
    minW: w.minW,
    minH: w.minH,
  }))
}

export function WidgetGrid({ widgets, onLayoutChange, renderWidget, availableWidgets }: WidgetGridProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [showAddMenu, setShowAddMenu] = useState(false)
  const { width, containerRef } = useContainerWidth()

  const handleLayoutChange = useCallback(
    (newLayout: Layout, _layouts: ResponsiveLayouts) => {
      if (!isEditing) return
      const updated = widgets.map((widget) => {
        const layoutItem = newLayout.find((l: LayoutItem) => l.i === widget.i)
        if (layoutItem) {
          return { ...widget, x: layoutItem.x, y: layoutItem.y, w: layoutItem.w, h: layoutItem.h }
        }
        return widget
      })
      onLayoutChange(updated)
    },
    [isEditing, widgets, onLayoutChange],
  )

  const handleRemove = useCallback(
    (id: string) => {
      onLayoutChange(widgets.filter((w) => w.i !== id))
    },
    [widgets, onLayoutChange],
  )

  const handleAdd = useCallback(
    (type: string, defaultW: number, defaultH: number) => {
      const maxY = widgets.reduce((max, w) => Math.max(max, w.y + w.h), 0)
      const newWidget: WidgetConfig = {
        i: `${type}-${Date.now()}`,
        type,
        x: 0,
        y: maxY,
        w: defaultW,
        h: defaultH,
      }
      onLayoutChange([...widgets, newWidget])
      setShowAddMenu(false)
    },
    [widgets, onLayoutChange],
  )

  const layoutItems = widgetConfigsToLayout(
    isEditing ? widgets : widgets.map((w) => ({ ...w, static: true } as WidgetConfig)),
  )

  return (
    <div ref={containerRef as React.RefObject<HTMLDivElement>}>
      {/* Add widget toolbar (visible only in edit mode) */}
      {isEditing && availableWidgets && (
        <div className="flex items-center justify-end gap-2 mb-3">
          <div className="relative">
            <button
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 border border-brand-200 rounded hover:bg-brand-50 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Widget hinzufügen
            </button>
            {showAddMenu && (
              <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50 min-w-[200px]">
                {availableWidgets.map((w) => (
                  <button
                    key={w.type}
                    onClick={() => handleAdd(w.type, w.defaultW, w.defaultH)}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    {w.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Anpassen button in footer */}
      {document.getElementById('footer-portal') && createPortal(
        <button
          onClick={() => setIsEditing(!isEditing)}
          className={`flex items-center gap-1 px-2.5 py-0.5 text-xs font-medium rounded transition-colors ml-auto ${
            isEditing
              ? 'bg-brand-500 text-white hover:bg-brand-600'
              : 'text-gray-500 border border-gray-200 hover:bg-gray-50'
          }`}
        >
          {isEditing ? (
            <>
              <Check className="w-3 h-3" />
              Fertig
            </>
          ) : (
            <>
              <Pencil className="w-3 h-3" />
              Anpassen
            </>
          )}
        </button>,
        document.getElementById('footer-portal')!,
      )}

      {/* Grid */}
      {width > 0 && (
        <ResponsiveGridLayout
          className="layout"
          width={width}
          layouts={{ lg: layoutItems }}
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
          cols={{ lg: 12, md: 8, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={40}
          dragConfig={{ enabled: isEditing, handle: '.drag-handle' }}
          resizeConfig={{ enabled: isEditing }}
          onLayoutChange={handleLayoutChange}
          margin={[12, 12] as const}
        >
          {widgets.map((config) => (
            <div key={config.i}>
              {renderWidget(config, isEditing, () => handleRemove(config.i))}
            </div>
          ))}
        </ResponsiveGridLayout>
      )}
    </div>
  )
}
